import { useDispatch, useSelector } from 'react-redux';
import {
  resetSessionState,
  clearSessionError,
  clearFetchSessionError,
  clearUpdateSessionError,
  setAuthentication,
  clearAuthentication,
  updateSessionId,
  setOrganizationId,
  resetOrganizationState,
  clearOrganizationError,
  resetUserState,
  clearUserError,
  resetFeaturesState,
  clearFeaturesError,
  setFeaturesLoading,
  setFeaturesData,
  setFeaturesError,
  resetSaveFeaturesState,
  clearSaveFeaturesError,
  setDashboardLoading,
  setDashboardData,
  setDashboardError,
  clearDashboardError,
  resetDashboardState,
  setEhrCredentials,
  clearEhrCredentials,
} from '../slices/onboardingSlice';
import {
  selectSessionId,
  selectIsCreatingSession,
  selectSessionError,
  selectSessionCreated,
  selectSessionData,
  selectIsFetchingSession,
  selectFetchSessionError,
  selectIsUpdatingSession,
  selectUpdateSessionError,
  selectIsAuthenticated,
  selectAuthenticatedUser,
  selectAuthTokens,
  selectOrganization,
  selectIsCreatingOrganization,
  selectOrganizationError,
  selectOrganizationCreated,
  selectUser,
  selectIsCreatingUser,
  selectUserError,
  selectUserCreated,
  selectFeatures,
  selectIsLoadingFeatures,
  selectFeaturesError,
  selectOrganizationId,
  selectIsSavingFeatures,
  selectSaveFeaturesError,
  selectFeaturesSaved,
  selectDashboardData,
  selectIsLoadingDashboard,
  selectDashboardError,
  selectEhrCredentials,
} from '../slices/onboardingSlice';

export const useOnboarding = () => {
  const dispatch = useDispatch();

  // Selectors
  const sessionId = useSelector(selectSessionId);
  const isCreatingSession = useSelector(selectIsCreatingSession);
  const sessionError = useSelector(selectSessionError);
  const sessionCreated = useSelector(selectSessionCreated);
  const sessionData = useSelector(selectSessionData);
  const isFetchingSession = useSelector(selectIsFetchingSession);
  const fetchSessionError = useSelector(selectFetchSessionError);
  const isUpdatingSession = useSelector(selectIsUpdatingSession);
  const updateSessionError = useSelector(selectUpdateSessionError);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const authenticatedUser = useSelector(selectAuthenticatedUser);
  const authTokens = useSelector(selectAuthTokens);
  const organization = useSelector(selectOrganization);
  const isCreatingOrganization = useSelector(selectIsCreatingOrganization);
  const organizationError = useSelector(selectOrganizationError);
  const organizationCreated = useSelector(selectOrganizationCreated);
  const user = useSelector(selectUser);
  const isCreatingUser = useSelector(selectIsCreatingUser);
  const userError = useSelector(selectUserError);
  const userCreated = useSelector(selectUserCreated);
  const features = useSelector(selectFeatures);
  const isLoadingFeatures = useSelector(selectIsLoadingFeatures);
  const featuresError = useSelector(selectFeaturesError);
  const organizationId = useSelector(selectOrganizationId);
  const isSavingFeatures = useSelector(selectIsSavingFeatures);
  const saveFeaturesError = useSelector(selectSaveFeaturesError);
  const featuresSaved = useSelector(selectFeaturesSaved);
  const dashboardData = useSelector(selectDashboardData);
  const isLoadingDashboard = useSelector(selectIsLoadingDashboard);
  const dashboardError = useSelector(selectDashboardError);
  const ehrCredentials = useSelector(selectEhrCredentials);

  // Action dispatchers
  const resetSession = () => dispatch(resetSessionState());
  const clearSessionErrorState = () => dispatch(clearSessionError());
  const clearFetchSessionErrorState = () => dispatch(clearFetchSessionError());
  const clearUpdateSessionErrorState = () => dispatch(clearUpdateSessionError());
  const setAuthenticationState = (data) => dispatch(setAuthentication(data));
  const clearAuthenticationState = () => dispatch(clearAuthentication());
  const updateSessionIdState = (id) => dispatch(updateSessionId(id));
  const setOrganizationIdState = (id) => dispatch(setOrganizationId(id));
  const resetOrganization = () => dispatch(resetOrganizationState());
  const clearOrganizationErrorState = () => dispatch(clearOrganizationError());
  const resetUser = () => dispatch(resetUserState());
  const clearUserErrorState = () => dispatch(clearUserError());
  const resetFeatures = () => dispatch(resetFeaturesState());
  const clearFeaturesErrorState = () => dispatch(clearFeaturesError());
  const setFeaturesLoadingState = (loading) => dispatch(setFeaturesLoading(loading));
  const setFeaturesDataState = (data) => dispatch(setFeaturesData(data));
  const setFeaturesErrorState = (error) => dispatch(setFeaturesError(error));
  const resetSaveFeatures = () => dispatch(resetSaveFeaturesState());
  const clearSaveFeaturesErrorState = () => dispatch(clearSaveFeaturesError());
  const setDashboardLoadingState = (loading) => dispatch(setDashboardLoading(loading));
  const setDashboardDataState = (data) => dispatch(setDashboardData(data));
  const setDashboardErrorState = (error) => dispatch(setDashboardError(error));
  const clearDashboardErrorState = () => dispatch(clearDashboardError());
  const resetDashboard = () => dispatch(resetDashboardState());
  const setEhrCredentialsState = (credentials) => dispatch(setEhrCredentials(credentials));
  const clearEhrCredentialsState = () => dispatch(clearEhrCredentials());

  return {
    // State
    sessionId,
    isCreatingSession,
    sessionError,
    sessionCreated,
    sessionData,
    isFetchingSession,
    fetchSessionError,
    isUpdatingSession,
    updateSessionError,
    isAuthenticated,
    authenticatedUser,
    authTokens,
    organization,
    isCreatingOrganization,
    organizationError,
    organizationCreated,
    user,
    isCreatingUser,
    userError,
    userCreated,
    features,
    isLoadingFeatures,
    featuresError,
    organizationId,
    isSavingFeatures,
    saveFeaturesError,
    featuresSaved,
    dashboardData,
    isLoadingDashboard,
    dashboardError,
    ehrCredentials,

    // Actions
    resetSession,
    clearSessionErrorState,
    clearFetchSessionErrorState,
    clearUpdateSessionErrorState,
    setAuthenticationState,
    clearAuthenticationState,
    updateSessionIdState,
    setOrganizationIdState,
    resetOrganization,
    clearOrganizationErrorState,
    resetUser,
    clearUserErrorState,
    resetFeatures,
    clearFeaturesErrorState,
    setFeaturesLoadingState,
    setFeaturesDataState,
    setFeaturesErrorState,
    resetSaveFeatures,
    clearSaveFeaturesErrorState,
    setDashboardLoadingState,
    setDashboardDataState,
    setDashboardErrorState,
    clearDashboardErrorState,
    resetDashboard,
    setEhrCredentialsState,
    clearEhrCredentialsState,
  };
};

export default useOnboarding;
